import axios from "axios"
import QueryString from "qs"

import type { BookingSearch } from "@/lib/bookingSearch"
import { destructureBookingSearchCacheKeys } from "@/lib/bookingSearch/cacheKeys/destructure"
import type { Businesses } from "@/lib/businesses"
import type { ErrorHandling } from "@/lib/error_handling"
import { withRetries } from "@/lib/error_handling/withRetries"
import type { Geolocation } from "@/lib/geolocation"
import { getRelativeDay } from "@/lib/time"
import { toDateString } from "@/lib/time/toDateString"

type Filter = {
  end_date: string
  position: Geolocation.Position
  range: number
  service: string
  start_date: string
}

export const fetchBusinesses = async ({
  keys,
  onFailure,
}: {
  keys: BookingSearch.Cache.Keys
  onFailure: ErrorHandling.Functions.OnFatalError
}): Promise<Businesses.Business[]> => {
  const { distance, position, service } =
    destructureBookingSearchCacheKeys(keys)

  const filter: Partial<Filter> = {
    end_date: toDateString(
      getRelativeDay({ baseDate: new Date(), dayAdjustment: 6 })
    ),
    position,
    range: distance,
    // We represent the hand therapy service as "hand_therapy" in the frontend,
    // but the backend API has "hand therapy" stored in its database entries.
    service: service.replace(`hand_therapy`, `hand therapy`),
    start_date: toDateString(new Date()),
  }

  const completion = await withRetries({
    getFallbackResult: async () => {
      return []
    },
    onFatalError: onFailure,
    process: async () => {
      const {
        data: { data: businesses },
      } = await axios.get<{ data: Businesses.Business[] }>(
        `/api/get-appointments`,
        {
          params: { filter },
          paramsSerializer: (params) => {
            return QueryString.stringify(params, { arrayFormat: `brackets` })
          },
        }
      )
      return businesses
    },
    processName: `fetchBusinessOfferings`,
  })

  return completion.result || []
}
